import { useMemo } from 'react'
import { CatmullRomCurve3, Vector3 } from 'three'
import { CabecaHumana3D, CorpoHumano3D } from './CorpoHumano3D'
import { PecaDetalhe3D } from './PecaDetalhe3D'

export type NivelBombeiro =
  'bombeiroNivelA' | 'bombeiroNivelB' | 'bombeiroNivelC'
const CORES = {
  bombeiroNivelA: { suit: '#e7dc49', acento: '#a89f35' },
  bombeiroNivelB: { suit: '#76b689', acento: '#426b50' },
  bombeiroNivelC: { suit: '#f2f3ef', acento: '#adb7bc' },
}

function MascaraFacial3D() {
  return (
    <group name="mascara-facial-inteira">
      <PecaDetalhe3D
        posicao={[0, 1.64, 0.108]}
        tamanho={[0.225, 0.255, 0.08]}
        cor="#263438"
        raio={0.06}
      />
      <PecaDetalhe3D
        posicao={[0, 1.68, 0.153]}
        tamanho={[0.181, 0.152, 0.026]}
        cor="#365965"
        raio={0.04}
        metal={0.3}
        rugosidade={0.12}
      />
      <PecaDetalhe3D
        posicao={[0, 1.57, 0.177]}
        tamanho={[0.1, 0.074, 0.07]}
        cor="#1c292c"
        raio={0.025}
      />
      <PecaDetalhe3D
        posicao={[0, 1.725, 0.17]}
        tamanho={[0.12, 0.01, 0.008]}
        cor="#8baab1"
        raio={0.003}
        metal={0.3}
      />
      {[-1, 1].map((lado) => (
        <PecaDetalhe3D
          key={lado}
          posicao={[lado * 0.11, 1.64, -0.005]}
          tamanho={[0.016, 0.035, 0.22]}
          cor="#293336"
          raio={0.005}
          rotacao={[0.1, 0, 0]}
        />
      ))}
    </group>
  )
}

function EprExterno3D() {
  const mangueira = useMemo(
    () =>
      new CatmullRomCurve3([
        new Vector3(0.09, 1.42, -0.29),
        new Vector3(0.26, 1.47, -0.14),
        new Vector3(0.3, 1.31, 0.19),
        new Vector3(0.12, 1.4, 0.23),
        new Vector3(0, 1.57, 0.22),
      ]),
    [],
  )
  return (
    <group name="epr-externo">
      <PecaDetalhe3D
        posicao={[0, 1.17, -0.18]}
        tamanho={[0.29, 0.55, 0.09]}
        cor="#243235"
        raio={0.03}
      />
      <mesh name="cilindro-ar-vertical" position={[0, 1.16, -0.295]} castShadow>
        <capsuleGeometry args={[0.105, 0.39, 4, 14]} />
        <meshStandardMaterial
          color="#c4cbbd"
          metalness={0.45}
          roughness={0.45}
        />
      </mesh>
      {[0.99, 1.3].map((y) => (
        <mesh key={y} position={[0, y, -0.295]} rotation={[Math.PI / 2, 0, 0]}>
          <torusGeometry args={[0.107, 0.015, 6, 14]} />
          <meshStandardMaterial color="#293538" roughness={0.8} />
        </mesh>
      ))}
      <mesh position={[0, 1.47, -0.295]}>
        <cylinderGeometry args={[0.027, 0.035, 0.07, 10]} />
        <meshStandardMaterial color="#9d9c60" metalness={0.7} roughness={0.4} />
      </mesh>
      {[-1, 1].map((lado) => (
        <PecaDetalhe3D
          key={lado}
          posicao={[lado * 0.15, 1.21, 0.148]}
          tamanho={[0.047, 0.46, 0.035]}
          cor="#2b383c"
          raio={0.012}
          rotacao={[0, 0, lado * -0.15]}
        />
      ))}
      <PecaDetalhe3D
        posicao={[0, 0.98, 0]}
        tamanho={[0.405, 0.055, 0.29]}
        cor="#2b383c"
        raio={0.018}
      />
      <PecaDetalhe3D
        posicao={[0, 0.98, 0.157]}
        tamanho={[0.07, 0.045, 0.025]}
        cor="#a9b5b6"
        metal={0.6}
        raio={0.008}
      />
      <mesh>
        <tubeGeometry args={[mangueira, 16, 0.012, 6, false]} />
        <meshStandardMaterial color="#1c282c" roughness={0.8} />
      </mesh>
    </group>
  )
}

export function FiguraBombeiro3D({ nivel }: { nivel: NivelBombeiro }) {
  const { suit, acento } = CORES[nivel]
  const encapsulado = nivel === 'bombeiroNivelA'
  return (
    <group name={nivel}>
      <CorpoHumano3D cor={suit} />
      <PecaDetalhe3D
        posicao={[encapsulado ? 0.07 : 0, 1.17, 0.153]}
        tamanho={[0.025, 0.48, 0.02]}
        cor={acento}
        raio={0.008}
        rotacao={[0, 0, encapsulado ? -0.18 : 0]}
      />
      {[-1, 1].map((lado) => (
        <PecaDetalhe3D
          key={lado}
          posicao={[lado * 0.107, 0.26, 0]}
          tamanho={[0.166, 0.055, 0.165]}
          cor={acento}
          raio={0.025}
        />
      ))}
      {encapsulado ? (
        <group name="traje-encapsulado">
          <PecaDetalhe3D
            posicao={[0, 1.18, -0.22]}
            tamanho={[0.38, 0.59, 0.27]}
            cor={suit}
            raio={0.12}
          />
          <mesh position={[0, 1.64, 0]} scale={[1, 1.12, 1]} castShadow>
            <sphereGeometry args={[0.235, 18, 14]} />
            <meshStandardMaterial color={suit} roughness={0.6} />
          </mesh>
          <PecaDetalhe3D
            posicao={[0, 1.665, 0.213]}
            tamanho={[0.34, 0.225, 0.067]}
            cor={acento}
            raio={0.035}
          />
          <PecaDetalhe3D
            posicao={[0, 1.67, 0.252]}
            tamanho={[0.29, 0.177, 0.025]}
            cor="#254752"
            raio={0.035}
            metal={0.35}
            rugosidade={0.13}
          />
          <PecaDetalhe3D
            posicao={[0, 1.72, 0.267]}
            tamanho={[0.22, 0.012, 0.008]}
            cor="#90abb2"
            raio={0.003}
          />
          {[-1, 1].map((lado) => (
            <mesh
              key={lado}
              position={[lado * 0.205, 1.27, -0.128]}
              rotation={[0, 0, Math.PI / 2]}
            >
              <cylinderGeometry args={[0.032, 0.032, 0.022, 10]} />
              <meshStandardMaterial color="#3e4945" roughness={0.65} />
            </mesh>
          ))}
        </group>
      ) : (
        <>
          <CabecaHumana3D />
          <mesh
            position={[0, 1.645, -0.025]}
            scale={[1.08, 1.15, 1.04]}
            castShadow
          >
            <sphereGeometry args={[0.145, 16, 12]} />
            <meshStandardMaterial color={suit} roughness={0.8} />
          </mesh>
          <MascaraFacial3D />
          {nivel === 'bombeiroNivelB' ? (
            <EprExterno3D />
          ) : (
            <group name="filtros-mascara">
              {[-1, 1].map((lado) => (
                <group
                  key={lado}
                  position={[lado * 0.125, 1.575, 0.17]}
                  rotation={[Math.PI / 2, 0, lado * -0.2]}
                >
                  <mesh castShadow>
                    <cylinderGeometry args={[0.043, 0.047, 0.07, 12]} />
                    <meshStandardMaterial color="#303b42" roughness={0.7} />
                  </mesh>
                  <mesh position={[0, 0.041, 0]}>
                    <cylinderGeometry args={[0.036, 0.036, 0.013, 12]} />
                    <meshStandardMaterial
                      color="#8a9196"
                      metalness={0.45}
                      roughness={0.6}
                    />
                  </mesh>
                </group>
              ))}
            </group>
          )}
        </>
      )}
    </group>
  )
}
